'use client';

import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { Input as ShadcnInput } from '@ui/components/ui/input';

import { cn } from '../../../lib/utils';
import { FormControl, FormItem, FormLabel, FormMessage } from '../form/Form';

interface FloatingInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label: string;
  required?: boolean;
  containerClassName?: string;
  inputClassName?: string;
  placeholder?: string;
  alwaysFloat?: boolean;
}

/**
 * FloatingInput integrates shadcn styled input with floating label UX.
 * Should be used within a <FormProvider> from react-hook-form.
 */
export const FloatingInput = ({
  ref,
  name,
  label,
  required,
  containerClassName,
  inputClassName,
  type = 'text',
  placeholder,
  alwaysFloat = false,
  onFocus: providedOnFocus,
  onBlur: providedOnBlur,
  ...rest
}: FloatingInputProps & { ref?: React.RefObject<HTMLInputElement | null> }) => {
  const {
    register,
    watch,
    formState: { errors }
  } = useFormContext();
  const [focused, setFocused] = React.useState(false);
  const hasValue = Boolean(watch(name));

  // Get props from react-hook-form register
  const rhfProps = register(name, { required });

  // Custom focus handler
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    providedOnFocus?.(e);
  };

  // Custom blur handler
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    rhfProps.onBlur(e);
    providedOnBlur?.(e);
  };

  return (
    <FormItem className={cn('relative', containerClassName)}>
      <FormControl>
        <ShadcnInput
          id={name}
          type={type}
          placeholder={placeholder}
          className={cn(
            'block w-full rounded-lg border p-3 transition-colors placeholder:text-white focus:placeholder:text-neutral-400 h-auto font-normal text-base md:text-sm',
            errors[name] && 'border-destructive focus:border-destructive',
            inputClassName
          )}
          {...rest} // Spread other props first
          {...rhfProps} // Spread RHF props (includes name, ref, onChange, original onBlur)
          onFocus={handleFocus} // Override with custom focus handler
          onBlur={handleBlur} // Override with custom blur handler
          // ref is handled by rhfProps spread
        />
      </FormControl>

      <FormLabel
        htmlFor={name}
        className={cn(
          'pointer-events-none absolute left-3 transition-all bg-background px-1 font-normal',
          // If alwaysFloat is true, always float up; otherwise, float up when focused or has value
          alwaysFloat || focused || hasValue
            ? 'top-0 -translate-y-1/2 text-xs text-foreground'
            : 'top-1/2 -translate-y-1/2 text-base text-muted-foreground',
          // Ensure label color changes on focus even if already floated due to value
          focused && 'text-foreground'
        )}
      >
        {label}
        {required && <span className="text-destructive">*</span>}
      </FormLabel>

      <FormMessage />
    </FormItem>
  );
};

FloatingInput.displayName = 'FloatingInput';
